import { Component, ViewChild } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import {
  AggregatedBillingInformationResultDto,
  BusinessReasonCode,
  ChargeType,
  DataHubStatus,
  MeteringPointType,
  PartyDto,
  PartyService,
  ProcessVariant,
  SettlementMethod,
} from '@app/api/dmc';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CardComponent } from '@shared/components/card/card.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import {
  settlementBillingInformationTable
} from '@shared/table-configs/settlement/settlement-billing-information.table';
import { TableComponent } from '@shared/components/table/table.component';
import {
  SettlementWholesaleRequestModalComponent
} from '@shared/modals/settlement/settlement-wholesale-request-modal/settlement-wholesale-request-modal.component';
import { settlementActionMenu, settlementBillingActionMenu } from '@views/settlement/settlement.action-menu';
import {
  SettlementWholesaleExportReportModalComponent
} from '@shared/modals/settlement/settlement-wholesale-export-report-modal/settlement-wholesale-export-report-modal.component';
import {
  SettlementBillingViewModalComponent
} from '@shared/modals/settlement/settlement-billing-view-modal/settlement-billing-view-modal.component';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { SortParam } from '@app/core/constants/sort-param';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ODataType } from '@app/core/services/odata/types/odata-type';

@Component({
  selector: 'app-settlement-billing-information',
  imports: [
    CardComponent,
    ButtonDirective,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    InputDirective,
    TableComponent,
    SelectDirective
  ],
  templateUrl: './settlement-billing-information.component.html',
  styleUrl: './settlement-billing-information.component.css'
})
export class SettlementBillingInformationComponent {
  @ViewChild('table', {static: false}) public table?: TableComponent<any>;

  public searchForm: FormGroup = new FormGroup({
    startDate: new FormControl(),
    endDate: new FormControl(),
    balanceSupplierIdentification: new FormControl(),
    businessReasonCode: new FormControl(),
    typeOfMeteringPoint: new FormControl(),
    settlementMethod: new FormControl(),
    chargeType: new FormControl(),
    chargeTypeId: new FormControl(),
    chargeOwnerEnergyParty: new FormControl(),
    processVariant: new FormControl(),
    dataHubStatus: new FormControl(),
  });

  /** Enums **/
  public balanceSupplierList: KeyValue[] = [];
  public meteringPointTypeList: KeyValue[] = sortByTranslation(enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointtype.'}));
  public settlementMethodList: KeyValue[] = enumToKeyValue(SettlementMethod, { omitKeys: ['NotSpecified'], prefixValue: 'enum.settlementmethod.'});
  public chargeTypeList: KeyValue[] = enumToKeyValue(ChargeType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.chargetype.'});
  public chargeOwnerEnergyPartyList: KeyValue[] = [];
  public businessReasonCodeList: KeyValue[] = enumToKeyValue(BusinessReasonCode, { includeKeys: ['SecondSettlement', 'CorrectionSettlement'], prefixValue: 'enum.settlement.businessreasoncode.' });
  public processVariantList: KeyValue[] = enumToKeyValue(ProcessVariant, { omitKeys: ['NotSpecified', 'FirstSettlement', 'SecondSettlement'], prefixValue: 'enum.settlement.processvariant.'});
  public dataHubStatusList: KeyValue[] = enumToKeyValue(DataHubStatus, { omitKeys: ['NotSpecified'], prefixValue: 'enum.datahub.status.'});

  /** Carriers **/
  private balanceSuppliers: PartyDto[] = [];
  private chargeInformationParties: PartyDto[] = [];

  /** Actions **/
  public actions: MenuAction[] = [
    {
      ...settlementActionMenu[0],
      callback: () => this.openViewModal(),
      disabled: () => this.selectedElements.length !== 1,
    },
    {
      ...settlementActionMenu[1],
      callback: () => this.openRequestModal(),
    }
  ]

  /** Table **/
  public tableConfig: TableConfig<AggregatedBillingInformationResultDto> = {
    columns: [
      ...settlementBillingInformationTable.columns
    ],
    id: 'settlement-billing-information-table',
    title: 'settlement.billing.title',
    exportable: true,
    copyable: true,
    floatingHeader: true,
    saveChanges: true,
    selectable: true,
    selectableType: 'single',
    oData: true,
    pageSizeSelection: true,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.searchCallback(pageSize, page, order),
    contextActions: [
      this.actions[0],
    ]
  }

  public selectedElements: AggregatedBillingInformationResultDto[] = [];


  constructor(
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly oDataService: ODataService,
    private readonly partyService: PartyService,
  ) {
    this.headerMenuService.setActions(settlementBillingActionMenu,
      {
        view: () => this.openViewModal(),
        request: () => this.openRequestModal(),
        exportReport: () => this.exportWholesaleReport(),
      },
      {
        view: () => this.selectedElements.length !== 1
      }
    );

    /** Populate Necessary Lists **/
    this.partyService.partyGetBalanceSuppliersGet().subscribe((data: PartyDto[]) => {
      this.balanceSuppliers = data ?? [];

      this.balanceSupplierList = this.balanceSuppliers.map((item: PartyDto) => ({
        key: item.identification ?? '',
        value: item.name ?? '',
      }))
    });

    this.partyService.partyGetChargeInformationPartiesGet().subscribe((data: PartyDto[]) => {
      this.chargeInformationParties = data ?? [];

      this.chargeOwnerEnergyPartyList = this.chargeInformationParties.map((item: PartyDto) => ({
        key: item.identification ?? '',
        value: item.name ?? '',
      }))
    });
  }

  public searchCallback(pageSize: number, page: number, sort?: SortParam) {
    const data = this.searchForm.value;

    const query = this.oDataService.dmc.query()
      .paginate(page, pageSize)
      .count();

    if (data.startDate)                     query.param('StartDate',                   data.startDate);
    if (data.endDate)                       query.param('EndDate',                     data.endDate);
    if (data.balanceSupplierIdentification) query.param('BalanceSupplierIdentification', data.balanceSupplierIdentification);
    if (data.typeOfMeteringPoint)           query.param('TypeOfMeteringPoint',         data.typeOfMeteringPoint);
    if (data.settlementMethod)              query.param('SettlementMethod',            data.settlementMethod);
    if (data.businessReasonCode)            query.param('BusinessReasonCode',          data.businessReasonCode);
    if (data.processVariant)                query.param('ProcessVariant',              data.processVariant);
    if (data.dataHubStatus)                 query.param('DataHubStatus',               data.dataHubStatus);
    if (data.chargeType)                    query.param('ChargeType',                  data.chargeType);
    if (data.chargeTypeId)                  query.param('ChargeTypeId',                data.chargeTypeId);
    if (data.chargeOwnerEnergyParty)        query.param('ChargeOwnerEnergyParty',      data.chargeOwnerEnergyParty);

    if (sort) {
      query.orderBy(`${sort.sortField} ${sort.sortDirection}`);
    }

    return this.oDataService.dmc.getCollection<AggregatedBillingInformationResultDto>(
      ODataType.queryAggregatedBillingInformation,
      query,
    );
  }

  public exportWholesaleReport(): void {
    this.modalService.show({
      component: SettlementWholesaleExportReportModalComponent,
      title: 'settlement.wholesale.exportReport.title',
      actions: [
        {type: 'primary', label: 'settlement.wholesale.exportReport.export', method: 'submit'},
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
    });
  }

  public openRequestModal(): void {
    this.modalService.show({
      component: SettlementWholesaleRequestModalComponent,
      title: 'settlement.wholesale.request.title',
      actions: [
        { type: 'primary', label: 'system.button.request', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        monthlyAggregations: true,
      },
    })
  }

  public openViewModal(): void {
    if (this.selectedElements.length === 1) {
      this.modalService.show({
        component: SettlementBillingViewModalComponent,
        title: 'settlement.wholesale.view.title',
        actions: [
          { type: 'secondary', label: 'system.button.close', closeOnClick: true },
        ],
        params: {
          id: this.selectedElements[0].aggregatedBillingInformationId
        }
      })
    }

  }

}
